import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import Sospeso from "@/pages/Sospeso";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import Campaigns from "@/pages/Campaigns";
import Sms from "@/pages/Sms";
import Otp from "@/pages/Otp";
import Templates from "@/pages/Templates";
import TemplateEditor from "@/pages/TemplateEditor";
import Consent from "@/pages/Consent";
import AIAgent from "@/pages/AIAgent";
import Chat from "@/pages/Chat";
import Lists from "@/pages/Lists";
import Forms from "@/pages/Forms";
import FormBuilder from "@/pages/FormBuilder";
import FormResponses from "@/pages/FormResponses";
import FormAnalytics from "@/pages/FormAnalytics";
import Automazioni from "@/pages/Automazioni";
import Integrazioni from "@/pages/Integrazioni";
import EmailTransazionali from "@/pages/EmailTransazionali";
import Report from "@/pages/Report";
import Manuale from "@/pages/Manuale";
import Privacy from "@/pages/Privacy";
import PreferencePortal from "@/pages/PreferencePortal";
import Unsubscribe from "@/pages/Unsubscribe";
import DataDeletion from "@/pages/DataDeletion";
import Terms from "@/pages/Terms";
import Canali from "@/pages/Canali";
import AccountMeta from "@/pages/AccountMeta";
import SuperAdmin from "@/pages/SuperAdmin";
import Fatturazione from "@/pages/Fatturazione";
import PanoramicaCanali from "@/pages/PanoramicaCanali";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import { SessionExpiredDialog } from "@/components/SessionExpiredDialog";

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]">
    <div className="h-10 w-10 rounded-full border-2 border-[#10B981] border-t-transparent animate-spin" />
  </div>
);

function Protected({ children }) {
  const { user } = useAuth();
  const [tenant, setTenant] = useState(null);
  useEffect(() => {
    if (user && user.role !== "admin") api.get("/me/tenant-status").then((r) => setTenant(r.data)).catch(() => setTenant({ status: "attivo" }));
    else if (user) setTenant({ status: "attivo" });
  }, [user]);
  if (user === null || (user && tenant === null)) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (tenant.status === "sospeso") return <Navigate to="/sospeso" replace />;
  return <Layout>{tenant.demo && (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-xs sm:text-sm text-amber-900" data-testid="demo-banner">
      <strong>META REVIEW DEMO</strong> — ambiente dimostrativo: tutti i contatti, template, campagne e chat sono dati fittizi etichettati [DEMO]. Nessun messaggio viene inviato tramite le API Meta, SMS o email. / <em>Demo environment: fictitious data, no messages are sent through Meta APIs.</em>
    </div>)}{children}</Layout>;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function RootRoute() {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Landing />;
  return <Protected><Dashboard /></Protected>;
}

function LoginRoute() {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loader />;
  if (user) {
    const next = new URLSearchParams(loc.search).get("next");
    return <Navigate to={next && next.startsWith("/") && !next.startsWith("//") ? next : "/"} replace />;
  }
  return <Login />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <SessionExpiredDialog />
          <ErrorBoundary>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/p/:token" element={<PreferencePortal />} />
            <Route path="/u/:token" element={<Unsubscribe />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/termini" element={<Terms />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/sospeso" element={<Sospeso />} />
            <Route path="/" element={<RootRoute />} />
            <Route path="/rubrica" element={<Protected><Contacts /></Protected>} />
            <Route path="/liste" element={<Protected><Lists /></Protected>} />
            <Route path="/form" element={<Protected><Forms /></Protected>} />
            <Route path="/form/analytics" element={<Protected><FormAnalytics /></Protected>} />
            <Route path="/form/:id" element={<Protected><FormBuilder /></Protected>} />
            <Route path="/form/:id/risposte" element={<Protected><FormResponses /></Protected>} />
            <Route path="/campagne" element={<Protected><Campaigns /></Protected>} />
            <Route path="/sms" element={<Protected><Sms /></Protected>} />
            <Route path="/otp" element={<Protected><Otp /></Protected>} />
            <Route path="/template" element={<Protected><Templates /></Protected>} />
            <Route path="/template/nuovo" element={<Protected><TemplateEditor /></Protected>} />
            <Route path="/template/:id" element={<Protected><TemplateEditor /></Protected>} />
            <Route path="/consensi" element={<Protected><Consent /></Protected>} />
            <Route path="/assistente" element={<Protected><AIAgent /></Protected>} />
            <Route path="/chat" element={<Protected><Chat /></Protected>} />
            <Route path="/automazioni" element={<Protected><Automazioni /></Protected>} />
            <Route path="/integrazioni" element={<Protected><Integrazioni /></Protected>} />
            <Route path="/email" element={<Protected><EmailTransazionali /></Protected>} />
            <Route path="/report" element={<Protected><Report /></Protected>} />
            <Route path="/manuale" element={<Protected><Manuale /></Protected>} />
            <Route path="/canali" element={<Protected><AdminOnly><Canali /></AdminOnly></Protected>} />
            <Route path="/account-meta" element={<Protected><AccountMeta /></Protected>} />
            <Route path="/fatturazione" element={<Protected><Fatturazione /></Protected>} />
            <Route path="/superadmin" element={<Protected><AdminOnly><SuperAdmin /></AdminOnly></Protected>} />
            <Route path="/panoramica-canali" element={<Protected><AdminOnly><PanoramicaCanali /></AdminOnly></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
